import { API_BASE } from "./config";

const elements = {
  archiveButton: document.querySelector("#archive-button"),
  contextRecordDescription: document.querySelector(
    "#context-record-description"
  ),
  contextRecordIndex: document.querySelector("#context-record-index"),
  contextRecordName: document.querySelector("#context-record-name"),
  contextRecordStatus: document.querySelector("#context-record-status"),
  coverPreview: document.querySelector("#cover-preview"),
  coverUpload: document.querySelector("#cover-upload"),
  deleteButton: document.querySelector("#delete-button"),
  draftButton: document.querySelector("#draft-button"),
  editorContextIndex: document.querySelector("#editor-context-index"),
  editorContextPublished: document.querySelector("#editor-context-published"),
  editorContextSave: document.querySelector("#editor-context-save"),
  editorContextUpdated: document.querySelector("#editor-context-updated"),
  editorTitle: document.querySelector("#editor-title"),
  fieldCategory: document.querySelector("#field-category"),
  fieldCountry: document.querySelector("#field-country"),
  fieldCoverImageUrl: document.querySelector("#field-cover-image-url"),
  fieldDescription: document.querySelector("#field-description"),
  fieldExternalUrl: document.querySelector("#field-external-url"),
  fieldName: document.querySelector("#field-name"),
  fieldRegion: document.querySelector("#field-region"),
  fieldScore: document.querySelector("#field-score"),
  fieldSource: document.querySelector("#field-source"),
  fieldTags: document.querySelector("#field-tags"),
  importButton: document.querySelector("#import-button"),
  importFile: document.querySelector("#import-file"),
  importStatus: document.querySelector("#import-status"),
  listSearch: document.querySelector("#list-search"),
  newRecordButton: document.querySelector("#new-record-button"),
  overviewActivity: document.querySelector("#overview-activity"),
  overviewSummary: document.querySelector("#overview-summary"),
  preview: document.querySelector("#record-preview"),
  publishButton: document.querySelector("#publish-button"),
  recordContextStatusText: document.querySelector(
    "#record-context-status-text"
  ),
  recordContextTitle: document.querySelector("#record-context-title"),
  recordId: document.querySelector("#record-id"),
  recordIndexState: document.querySelector("#record-index-state"),
  recordList: document.querySelector("#record-list"),
  recordPublishedAt: document.querySelector("#record-published-at"),
  recordSaveState: document.querySelector("#record-save-state"),
  recordStatus: document.querySelector("#record-status"),
  recordStatusBadge: document.querySelector("#record-status-badge"),
  recordSummary: document.querySelector("#record-summary"),
  recordUpdatedAt: document.querySelector("#record-updated-at"),
  recordVisibilityText: document.querySelector("#record-visibility-text"),
  saveButton: document.querySelector("#save-button"),
  statusFilter: document.querySelector("#status-filter"),
  toast: document.querySelector("#admin-toast"),
  uploadButton: document.querySelector("#upload-button"),
  workflowSteps: Array.from(document.querySelectorAll("[data-workflow-step]")),
  workspaceSummary: document.querySelector("#workspace-summary"),
};

const editor = new window.Quill("#editor", {
  theme: "snow",
  modules: {
    toolbar: [
      [{ header: [2, 3, false] }],
      ["bold", "italic", "underline", "blockquote"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
      ["clean"],
    ],
  },
});

const state = {
  activeId: null,
  activeRecord: null,
  isDirty: false,
  lastPersistedSignature: "",
  listQuery: "",
  listStatus: "all",
  requestedId: new URLSearchParams(window.location.search).get("recordId"),
  summary: null,
  toastTimer: null,
};

function statusClassName(status) {
  return `status-badge status-badge--${status || "draft"}`;
}

function titleizeStatus(status) {
  const value = String(status || "draft").trim();
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatDate(timestamp) {
  if (!timestamp) {
    return "Not yet";
  }
  return new Date(timestamp * 1000)
    .toISOString()
    .slice(0, 16)
    .replace("T", " ");
}

function splitTags(input) {
  return String(input || "")
    .split(/[,\n|]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function normalizeHtml(input) {
  const value = String(input || "")
    .replace(/\s+/g, " ")
    .trim();
  return value === "<p><br></p>" ? "" : value;
}

function payloadSignature(payload) {
  return JSON.stringify({
    category: String(payload.category || "").trim(),
    contentHtml: normalizeHtml(payload.contentHtml),
    country: String(payload.country || "").trim(),
    coverImageUrl: String(payload.coverImageUrl || "").trim(),
    descriptionPlain: String(payload.descriptionPlain || "").trim(),
    externalUrl: String(payload.externalUrl || "").trim(),
    name: String(payload.name || "").trim(),
    region: String(payload.region || "").trim(),
    score: Number(payload.score) || 0,
    source: String(payload.source || "").trim(),
    status: String(payload.status || "draft").trim(),
    tags: splitTags(payload.tags || []).join("|"),
  });
}

function recordToPayload(record) {
  return {
    name: record.name,
    category: record.category,
    country: record.country,
    region: record.region,
    source: record.source,
    score: record.score,
    tags: record.tags || [],
    descriptionPlain: record.descriptionPlain || "",
    contentHtml: record.contentHtml || "",
    coverImageUrl: record.coverImageUrl || "",
    externalUrl: record.externalUrl || "",
    status: record.status || "draft",
  };
}

function createSummaryMarkup(summary) {
  return `
    <div class="summary-chip"><span>All</span><strong>${summary.all}</strong></div>
    <div class="summary-chip"><span>Draft</span><strong>${summary.draft}</strong></div>
    <div class="summary-chip"><span>Published</span><strong>${summary.published}</strong></div>
    <div class="summary-chip"><span>Archived</span><strong>${summary.archived}</strong></div>
  `;
}

function setText(element, value) {
  if (element) {
    element.textContent = value;
  }
}

function setStatusBadge(element, status) {
  if (!element) {
    return;
  }

  element.className = statusClassName(status);
  element.textContent = status;
}

function setContextChip(element, text, tone = "muted") {
  if (!element) {
    return;
  }

  element.className = `context-chip context-chip--${tone}`;
  element.textContent = text;
}

function summarizeRecord(payload) {
  const summary =
    payload.descriptionPlain ||
    "Start a new draft or select an existing record to continue editing.";
  return summary.length > 120 ? `${summary.slice(0, 117)}...` : summary;
}

function currentVisibility(status, isDirty) {
  if (status !== "published") {
    return "Hidden until published";
  }

  return isDirty
    ? "Published snapshot live, edits pending sync"
    : "Visible in search preview";
}

function currentIndexTone(status, isDirty) {
  if (status !== "published") {
    return { text: "Index hidden", tone: "muted" };
  }

  if (isDirty) {
    return { text: "Index out of sync", tone: "warning" };
  }

  return { text: "Live in search", tone: "success" };
}

function currentSaveTone(isDirty) {
  return isDirty
    ? { text: "Unsaved changes", tone: "warning" }
    : { text: "No pending changes", tone: "success" };
}

function updateWorkflow(status, isDirty) {
  const mode = status === "published" ? "published" : "draft";

  elements.workflowSteps.forEach((step) => {
    step.classList.remove("is-active", "is-complete");
  });

  const createStep = elements.workflowSteps.find(
    (step) => step.dataset.workflowStep === "create"
  );
  const refineStep = elements.workflowSteps.find(
    (step) => step.dataset.workflowStep === "refine"
  );
  const publishStep = elements.workflowSteps.find(
    (step) => step.dataset.workflowStep === "publish"
  );
  const previewStep = elements.workflowSteps.find(
    (step) => step.dataset.workflowStep === "preview"
  );

  createStep?.classList.add("is-complete");

  if (mode === "draft") {
    refineStep?.classList.add("is-active");
  } else {
    refineStep?.classList.add("is-complete");
    publishStep?.classList.add("is-complete");
    previewStep?.classList.add(isDirty ? "is-active" : "is-complete");
  }

  if (mode === "draft" && !isDirty) {
    publishStep?.classList.add("is-active");
  }
}

function collectFormPayload() {
  return {
    name: elements.fieldName.value.trim(),
    category: elements.fieldCategory.value.trim(),
    country: elements.fieldCountry.value.trim(),
    region: elements.fieldRegion.value.trim(),
    source: elements.fieldSource.value.trim(),
    score: Number(elements.fieldScore.value) || 0,
    tags: splitTags(elements.fieldTags.value),
    descriptionPlain: elements.fieldDescription.value.trim(),
    contentHtml: editor.root.innerHTML,
    coverImageUrl: elements.fieldCoverImageUrl.value.trim(),
    externalUrl: elements.fieldExternalUrl.value.trim(),
    status: elements.recordStatus.value,
  };
}

async function request(path, options = {}) {
  const response = await fetch(`${API_BASE}${path}`, {
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    ...options,
  });

  const isJson = response.headers
    .get("content-type")
    ?.includes("application/json");
  const data = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    throw new Error(data.error || data || "Request failed");
  }

  return data;
}

function showToast(message) {
  clearTimeout(state.toastTimer);
  elements.toast.hidden = false;
  elements.toast.textContent = message;
  state.toastTimer = window.setTimeout(() => {
    elements.toast.hidden = true;
  }, 2800);
}

function emitRecordsChanged(reason, recordId = null) {
  window.dispatchEvent(
    new CustomEvent("records:changed", {
      detail: { reason, recordId },
    })
  );
}

function setStatus(status) {
  elements.recordStatus.value = status;
  setStatusBadge(elements.recordStatusBadge, status);
  setStatusBadge(elements.contextRecordStatus, status);
}

function syncUrl(recordId = null) {
  const url = new URL(window.location.href);
  if (recordId) {
    url.searchParams.set("recordId", recordId);
    if (!url.hash) {
      url.hash = "records";
    }
  } else {
    url.searchParams.delete("recordId");
  }
  window.history.replaceState({}, "", url);
}

function renderSummary(summary) {
  const markup = createSummaryMarkup(summary);
  state.summary = summary;
  [
    elements.recordSummary,
    elements.overviewSummary,
    elements.workspaceSummary,
  ].forEach((element) => {
    if (element) {
      element.innerHTML = markup;
    }
  });
}

function renderActivity(items) {
  if (!elements.overviewActivity) {
    return;
  }

  if (!items.length) {
    elements.overviewActivity.innerHTML = `<div class="activity-item"><small>No recent activity yet.</small></div>`;
    return;
  }

  elements.overviewActivity.innerHTML = items
    .slice(0, 4)
    .map(
      (record) => `
        <button class="activity-item" type="button" data-record-item="${record.id}">
          <div class="activity-item__row">
            <strong>${record.name}</strong>
            <span class="${statusClassName(record.status)}">${record.status}</span>
          </div>
          <small>${record.category} · ${record.country} · Updated ${formatDate(record.updatedAt)}</small>
        </button>
      `
    )
    .join("");
}

function updateContextPanels() {
  const payload = collectFormPayload();
  const status = payload.status || "draft";
  const displayName = payload.name || "New Record";
  const lastSaved = formatDate(state.activeRecord?.updatedAt);
  const publishedAt = formatDate(state.activeRecord?.publishedAt);
  const visibility = currentVisibility(status, state.isDirty);
  const saveState = currentSaveTone(state.isDirty);
  const indexState = currentIndexTone(status, state.isDirty);

  setText(elements.contextRecordName, displayName);
  setText(elements.contextRecordDescription, summarizeRecord(payload));
  setText(elements.recordContextTitle, displayName);
  setText(elements.recordContextStatusText, titleizeStatus(status));
  setText(elements.recordUpdatedAt, lastSaved);
  setText(elements.recordVisibilityText, visibility);

  setContextChip(elements.contextRecordIndex, indexState.text, indexState.tone);
  setContextChip(elements.recordSaveState, saveState.text, saveState.tone);
  setContextChip(elements.recordIndexState, indexState.text, indexState.tone);
  setContextChip(elements.editorContextSave, saveState.text, saveState.tone);
  setContextChip(elements.editorContextIndex, indexState.text, indexState.tone);
  setContextChip(
    elements.editorContextUpdated,
    `Last saved: ${lastSaved}`,
    "muted"
  );
  setContextChip(
    elements.editorContextPublished,
    `Published: ${publishedAt}`,
    "muted"
  );
  setContextChip(
    elements.recordPublishedAt,
    `Published: ${publishedAt}`,
    "muted"
  );
  updateWorkflow(status, state.isDirty);
}

function updateDirtyState() {
  const nextSignature = payloadSignature(collectFormPayload());
  state.isDirty = nextSignature !== state.lastPersistedSignature;
  updateContextPanels();
}

function renderPreview() {
  const payload = collectFormPayload();
  const tags = payload.tags.map((tag) => `<span>${tag}</span>`).join("");
  const coverStyle = payload.coverImageUrl
    ? `style="background-image: url('${payload.coverImageUrl}')"`
    : "";

  elements.preview.innerHTML = `
    <article class="record-card">
      <div class="record-card__cover ${payload.coverImageUrl ? "has-image" : ""}" ${coverStyle}>
        <div class="record-card__cover-badge">${payload.category || "category"}</div>
      </div>
      <div class="record-card__body">
        <h2 class="record-card__title">${payload.name || "Untitled record"}</h2>
        <div class="record-card__meta">
          <span>${payload.country || "Country"}</span>
          <span>${payload.region || "Region"}</span>
        </div>
        <p class="record-card__description">${payload.descriptionPlain || "Summary preview will appear here."}</p>
        <div class="record-card__tags">${tags}</div>
        <div class="record-card__footer">
          <span>Source: ${payload.source || "manual_entry"}</span>
          <span>Status: ${payload.status}</span>
          <span>Score: ${payload.score}</span>
        </div>
      </div>
    </article>
  `;

  elements.coverPreview.style.backgroundImage = payload.coverImageUrl
    ? `url('${payload.coverImageUrl}')`
    : "";

  updateDirtyState();
}

function fillForm(record) {
  state.activeId = record.id;
  state.activeRecord = record;
  state.requestedId = record.id;
  elements.recordId.value = record.id;
  elements.editorTitle.textContent = record.name;
  elements.fieldName.value = record.name;
  elements.fieldCategory.value = record.category;
  elements.fieldCountry.value = record.country;
  elements.fieldRegion.value = record.region;
  elements.fieldSource.value = record.source;
  elements.fieldScore.value = record.score;
  elements.fieldTags.value = (record.tags || []).join(", ");
  elements.fieldDescription.value = record.descriptionPlain || "";
  elements.fieldCoverImageUrl.value = record.coverImageUrl || "";
  elements.fieldExternalUrl.value = record.externalUrl || "";
  editor.root.innerHTML = record.contentHtml || "";
  setStatus(record.status || "draft");
  syncUrl(record.id);
  state.lastPersistedSignature = payloadSignature(recordToPayload(record));
  state.isDirty = false;
  renderPreview();
  updateActionState();
}

function resetForm() {
  state.activeId = null;
  state.activeRecord = null;
  state.requestedId = null;
  elements.recordId.value = "";
  elements.editorTitle.textContent = "New Record";
  elements.fieldName.value = "";
  elements.fieldCategory.value = "";
  elements.fieldCountry.value = "";
  elements.fieldRegion.value = "";
  elements.fieldSource.value = "manual_entry";
  elements.fieldScore.value = "50";
  elements.fieldTags.value = "";
  elements.fieldDescription.value = "";
  elements.fieldCoverImageUrl.value = "";
  elements.fieldExternalUrl.value = "";
  editor.root.innerHTML = "";
  setStatus("draft");
  syncUrl(null);
  state.lastPersistedSignature = payloadSignature(collectFormPayload());
  state.isDirty = false;
  renderPreview();
  updateActionState();
}

function renderRecordList(items) {
  if (!items.length) {
    elements.recordList.innerHTML = `<div class="record-list__item">No records match the current filter.</div>`;
    return;
  }

  elements.recordList.innerHTML = items
    .map(
      (record) => `
        <button class="record-list__item ${record.id === state.activeId ? "is-active" : ""}" type="button" data-record-item="${record.id}">
          <div class="record-list__item-header">
            <div class="record-list__title">${record.name}</div>
            <span class="record-list__score">Score ${record.score}</span>
          </div>
          <div class="record-list__meta">
            <span>${record.category}</span>
            <span>${record.country}</span>
            <span>${record.region}</span>
          </div>
          <div class="record-list__meta">
            <span class="${statusClassName(record.status)}">${record.status}</span>
            <span>${record.source}</span>
            <span>Updated ${formatDate(record.updatedAt)}</span>
          </div>
        </button>
      `
    )
    .join("");
}

async function loadRecord(id) {
  const record = await request(`/records/${id}`);
  fillForm(record);
}

async function loadRecords() {
  const result = await request(
    `/records?status=${encodeURIComponent(state.listStatus)}&q=${encodeURIComponent(
      state.listQuery
    )}&limit=100`
  );

  renderSummary(result.summary);
  renderActivity(result.items);
  renderRecordList(result.items);

  if (state.activeId) {
    const stillExists = result.items.find((item) => item.id === state.activeId);
    if (!stillExists) {
      resetForm();
    }
    return;
  }

  if (state.requestedId) {
    const requestedRecord = result.items.find(
      (item) => item.id === state.requestedId
    );
    if (requestedRecord) {
      await loadRecord(requestedRecord.id);
      return;
    }
  }

  if (result.items[0]) {
    await loadRecord(result.items[0].id);
  }
}

async function saveRecord(nextStatus = null) {
  const payload = collectFormPayload();
  payload.status = nextStatus || payload.status;

  const method = state.activeId ? "PUT" : "POST";
  const path = state.activeId ? `/records/${state.activeId}` : "/records";
  const record = await request(path, {
    method,
    body: JSON.stringify(payload),
  });

  fillForm(record);
  await loadRecords();
  emitRecordsChanged(nextStatus || "saved", record.id);
  showToast(nextStatus ? `Record moved to ${nextStatus}` : "Record saved");
}

async function deleteActiveRecord() {
  if (!state.activeId) {
    return;
  }

  if (!window.confirm("Delete this record permanently?")) {
    return;
  }

  await request(`/records/${state.activeId}`, { method: "DELETE" });
  resetForm();
  await loadRecords();
  emitRecordsChanged("deleted");
  showToast("Record deleted");
}

async function uploadCover() {
  const file = elements.coverUpload.files[0];
  if (!file) {
    return;
  }

  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(`${API_BASE}/uploads`, {
    method: "POST",
    body: formData,
  });
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || "Upload failed");
  }

  elements.fieldCoverImageUrl.value = data.url;
  renderPreview();
  showToast("Cover uploaded");
}

async function importBatch() {
  const file = elements.importFile.files[0];
  if (!file) {
    throw new Error("Choose a JSON or CSV file first");
  }

  const formData = new FormData();
  formData.append("file", file);
  formData.append("status", elements.importStatus.value);

  const response = await fetch(`${API_BASE}/import`, {
    method: "POST",
    body: formData,
  });
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || "Import failed");
  }

  await loadRecords();
  emitRecordsChanged("imported");
  showToast(`Imported ${data.imported} records`);
}

function updateActionState() {
  const hasRecord = Boolean(state.activeId);
  elements.deleteButton.disabled = !hasRecord;
}

function handleRecordSelection(event) {
  const button = event.target.closest("[data-record-item]");
  if (!button) {
    return;
  }

  loadRecord(button.dataset.recordItem)
    .then(() => {
      window.dispatchEvent(
        new CustomEvent("workspace:navigate", {
          detail: { view: "records", tab: "editor" },
        })
      );
      return loadRecords();
    })
    .catch((error) => showToast(error.message));
}

elements.newRecordButton.addEventListener("click", () => {
  resetForm();
});

elements.saveButton.addEventListener("click", () => {
  saveRecord().catch((error) => showToast(error.message));
});

elements.publishButton.addEventListener("click", () => {
  saveRecord("published").catch((error) => showToast(error.message));
});

elements.draftButton.addEventListener("click", () => {
  saveRecord("draft").catch((error) => showToast(error.message));
});

elements.archiveButton.addEventListener("click", () => {
  saveRecord("archived").catch((error) => showToast(error.message));
});

elements.deleteButton.addEventListener("click", () => {
  deleteActiveRecord().catch((error) => showToast(error.message));
});

elements.uploadButton.addEventListener("click", () => {
  uploadCover().catch((error) => showToast(error.message));
});

elements.importButton.addEventListener("click", () => {
  importBatch().catch((error) => showToast(error.message));
});

elements.recordList.addEventListener("click", handleRecordSelection);
elements.overviewActivity?.addEventListener("click", handleRecordSelection);

let searchTimer = null;
elements.listSearch.addEventListener("input", (event) => {
  clearTimeout(searchTimer);
  searchTimer = window.setTimeout(() => {
    state.listQuery = event.target.value.trim();
    loadRecords().catch((error) => showToast(error.message));
  }, 180);
});

elements.statusFilter.addEventListener("change", (event) => {
  state.listStatus = event.target.value;
  loadRecords().catch((error) => showToast(error.message));
});

[
  elements.fieldName,
  elements.fieldCategory,
  elements.fieldCountry,
  elements.fieldRegion,
  elements.fieldSource,
  elements.fieldScore,
  elements.fieldTags,
  elements.fieldDescription,
  elements.fieldCoverImageUrl,
  elements.fieldExternalUrl,
].forEach((input) => {
  input.addEventListener("input", renderPreview);
});

editor.on("text-change", renderPreview);

window.addEventListener("beforeunload", (event) => {
  if (!state.isDirty) {
    return;
  }

  event.preventDefault();
  event.returnValue = "";
});

resetForm();
loadRecords().catch((error) => showToast(error.message));
